import * as React from "react";
import * as ReactDOM from "react-dom";

import { usePortalRoot } from "@src/PortalRoot";
import classNames from "classnames";
import { useTransitionState } from "react-transition-state";

export const TOOLTIP_ALIGNMENTS = [
  "top",
  "topLeft",
  "topRight",
  "bottom",
  "bottomLeft",
  "right",
] as const;

interface SharedTooltipProps {
  readonly children: React.ReactNode;
  readonly align?: (typeof TOOLTIP_ALIGNMENTS)[number];
  readonly fixed?: boolean;
  readonly margin?: number;
  readonly enableInteraction?: boolean;
}
interface TooltipContainerProps extends SharedTooltipProps {
  readonly childRef: React.RefObject<HTMLElement | undefined>;
  readonly hovered?: boolean;
}

const TooltipContainer = ({
  margin = 4,
  align = "top",
  fixed = false,
  ...props
}: TooltipContainerProps) => {
  const portalRoot = usePortalRoot();
  const [childPosition, setChildPosition] = React.useState<{
    x: number;
    y: number;
  }>({
    x: props.childRef.current?.getBoundingClientRect().x ?? 0,
    y: props.childRef.current?.getBoundingClientRect().y ?? 0,
  });
  const [mousePosition, setMousePosition] = React.useState<{
    x: number;
    y: number;
  }>({
    x: 0,
    y: 0,
  });
  const [tooltipWidth, setTooltipWidth] = React.useState(0);
  const [tooltipHeight, setTooltipHeight] = React.useState(0);
  const [childSize, setChildSize] = React.useState({
    width: props.childRef.current?.getBoundingClientRect().width ?? 0,
    height: props.childRef.current?.getBoundingClientRect().height ?? 0,
  });

  const handler = React.useCallback(() => {
    if (props.childRef.current) {
      const { x, y, width, height } =
        props.childRef.current.getBoundingClientRect();
      setChildPosition({ x, y });
      setChildSize({ width, height });
    }
  }, [props.childRef]);

  const onMouseMove = React.useCallback(({ x, y }: MouseEvent) => {
    setMousePosition({ x, y });
  }, []);

  React.useEffect(handler, [handler, props.hovered]);

  React.useEffect(() => {
    if (!fixed) {
      document.addEventListener("mousemove", onMouseMove);
      return () => document.removeEventListener("mousemove", onMouseMove);
    }
  }, [onMouseMove, fixed]);

  const { children } = props;

  const style = React.useMemo(() => {
    if (fixed) {
      const offsetX = 18;
      const centerX = childPosition.x + childSize.width / 2;
      const centerY = childPosition.y + childSize.height / 2;
      const clampToViewport = (left: number) =>
        Math.max(
          margin,
          Math.min(left, window.innerWidth - tooltipWidth - margin)
        );
      switch (align) {
        case "topRight":
          return {
            left: `${clampToViewport(centerX - offsetX)}px`,
            bottom: `${window.innerHeight - childPosition.y + margin}px`,
          };
        case "topLeft":
          return {
            left: `${clampToViewport(centerX - tooltipWidth + offsetX)}px`,
            bottom: `${window.innerHeight - childPosition.y + margin}px`,
          };
        case "top":
          return {
            left: `${clampToViewport(centerX - tooltipWidth / 2)}px`,
            bottom: `${window.innerHeight - childPosition.y + margin}px`,
          };
        case "bottom":
          return {
            left: `${clampToViewport(centerX - tooltipWidth / 2)}px`,
            top: `${childPosition.y + childSize.height + margin}px`,
          };
        case "bottomLeft":
          return {
            left: `${clampToViewport(centerX - tooltipWidth + offsetX)}px`,
            top: `${childPosition.y + childSize.height + margin}px`,
          };
        case "right":
          return {
            left: `${clampToViewport(
              childPosition.x + childSize.width + margin
            )}px`,
            top: `${centerY - tooltipHeight / 2}px`,
          };
      }
    }
    switch (align) {
      case "topRight":
        return {
          left: `${mousePosition.x - 12}px`,
          bottom: `${window.innerHeight - mousePosition.y + 20}px`,
        };
      case "topLeft":
        return {
          left: `${mousePosition.x - tooltipWidth + 18}px`,
          bottom: `${window.innerHeight - mousePosition.y + 20}px`,
        };
      case "top":
        return {
          left: `${mousePosition.x - tooltipWidth / 2}px`,
          bottom: `${window.innerHeight - mousePosition.y + 20}px`,
        };
      case "bottom":
        return {
          left: `${mousePosition.x - tooltipWidth / 2}px`,
          top: `${mousePosition.y + 20}px`,
        };
      case "bottomLeft":
        return {
          left: `${mousePosition.x - tooltipWidth + 18}px`,
          top: `${mousePosition.y + 20}px`,
        };
      case "right":
        return {
          left: `${mousePosition.x + 20}px`,
          top: `${mousePosition.y - tooltipHeight / 2}px`,
        };
    }
  }, [
    childPosition,
    mousePosition,
    childSize,
    tooltipWidth,
    tooltipHeight,
    align,
    fixed,
    margin,
  ]);

  const [transitionState, toggle] = useTransitionState({
    timeout: { enter: 0, exit: 250 },
    mountOnEnter: true,
    unmountOnExit: true,
    preEnter: true,
  });

  const [tooltipHovered, setTooltipHovered] = React.useState(false);
  const isOpen =
    !!props.hovered || (!!props.enableInteraction && tooltipHovered);

  React.useEffect(() => {
    toggle(isOpen);
  }, [isOpen, toggle]);

  if (!portalRoot || !transitionState.isMounted) {
    return null;
  }

  return ReactDOM.createPortal(
    <div
      role="presentation"
      ref={(ref) => {
        if (!ref) return;
        const { width, height } = ref.getBoundingClientRect();
        setTooltipHeight(height);
        setTooltipWidth(width);
      }}
      className={classNames("fixed z-[999999] transition-opacity", {
        "pointer-events-none": !props.enableInteraction,
        "pointer-events-auto": props.enableInteraction,
        "opacity-100":
          transitionState.status === "entered" ||
          transitionState.status === "entering",
        "opacity-0":
          transitionState.status === "exiting" ||
          transitionState.status === "exited" ||
          transitionState.status === "preEnter",
      })}
      style={{ ...style }}
      onMouseEnter={
        props.enableInteraction ? () => setTooltipHovered(true) : undefined
      }
      onMouseLeave={
        props.enableInteraction ? () => setTooltipHovered(false) : undefined
      }
    >
      {children}
    </div>,
    portalRoot
  );
};

interface TooltipProps extends SharedTooltipProps {
  children: React.ReactElement & {
    ref?:
      | React.RefCallback<HTMLElement>
      | React.MutableRefObject<HTMLElement | undefined | null>;
  };
  readonly tooltip: React.ReactNode;
  readonly debug?: boolean;
  readonly dataTestId?: string;
  readonly enabled?: boolean; // Tooltip never shown when not enabled
}

const TooltipWrapper = ({
  children,
  debug,
  dataTestId = "text-button",
  tooltip,
  enabled = true,
  ...sharedProps
}: TooltipProps) => {
  const [hovered, setHovered] = React.useState<boolean>(false);

  const onMouseOver = () => {
    setHovered(true);
    children.props.onMouseOver?.();
  };

  const onMouseLeave = () => {
    if (!debug) setHovered(false);
    children.props.onMouseLeave?.();
  };

  const childRef = React.useRef<HTMLElement>();
  const handleRef = React.useCallback(
    (node: HTMLElement | null) => {
      if (node) {
        childRef.current = node;
        if (children.ref) {
          if (typeof children.ref === "function") {
            children.ref(node);
          } else {
            children.ref.current = node;
          }
        }
      }
    },
    [childRef, children]
  );

  return (
    <>
      {React.cloneElement(children, {
        onMouseOver,
        onMouseLeave,
        ref: handleRef,
      })}

      <TooltipContainer
        hovered={(enabled && hovered) || debug}
        childRef={childRef}
        data-testid={dataTestId}
        {...sharedProps}
      >
        {tooltip}
      </TooltipContainer>
    </>
  );
};

export default TooltipWrapper;
